import { createHead, VueHeadMixin } from '@vueuse/head';
import { createApp, getCurrentInstance } from 'vue';
import App from './App.vue';
import './style.css';
import store from './store.js';
import router from './router.js';
import posthog from 'posthog-js';
import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { FetchInstrumentation } from '@grafana/faro-instrumentation-fetch';
import { XHRInstrumentation } from '@grafana/faro-instrumentation-xhr';
import { VueTolgee } from '@tolgee/vue';

import tolgee from './tolgee.js';
import instance from '@/api/instance';
import popups from './plugins/popups/popups';
import vColor from './plugins/v-color.js';
import vScreen from './plugins/v-screen.js';
import 'plyr/dist/plyr.css';

import ui from '@/components/ui/ui.js';
import directives from '@/directives/index.js';
import { toFilename } from '@/lib/utils';

// LOCAL DEV ONLY: seed the `accessToken` flag the router guard checks, so the
// app can be opened on localhost without completing the Google sign-in flow.
// The guard only tests this key for presence (see router.js — the real login
// callback also stores the literal `true`), so this grants no privilege by
// itself: the server still decides who the caller is, and it only honours the
// matching DEV_AUTH_EMAIL bypass under NODE_ENV=development.
//
// `import.meta.env.DEV` is false in every `vite build`, so this whole block is
// dead code that tree-shaking strips from production bundles. It additionally
// requires VITE_DEV_AUTO_LOGIN='true', which is absent from .env.staging and
// .env.production.
if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_LOGIN === 'true') {
  if (!localStorage.getItem('accessToken')) localStorage.setItem('accessToken', 'true');
  console.warn('⚠ DEV auto-login active — router auth guard bypassed via VITE_DEV_AUTO_LOGIN.');
}

(async (_) => {
  try {
    await tolgee.run();
  } catch (err) {
    // i18n backend unreachable (TLS interception, offline, 5xx…). Tolgee still
    // falls back to the bundled staticData, so let the app mount rather than
    // letting an uncaught RecordFetchError white-screen the page.
    console.error('tolgee.run() failed, continuing with bundled translations', err);
    window?.faro?.api?.pushError?.(err instanceof Error ? err : new Error(String(err)));
  }
})();
const app = createApp(App);

const head = createHead();
app.mixin(VueHeadMixin);

app.use(router).use(head).use(VueTolgee, { tolgee }).use(store).use(popups).use(vScreen).use(vColor, {
  color0: '#EA5F47',
  color50: '#e1b74d',
  color100: '#4EA99C'
});

Object.entries(ui).forEach(([k, v]) => app.component(k, v));
Object.entries(directives).forEach(([k, v]) => app.directive(k, v));

// allowed in production only
if (import.meta.env.VITE_ENV == 'production') {
  posthog.init('phc_4sl8xHy07CaL8eySbBqLFwSaej0zXYBdg8G7RXHZFOm', {
    api_host: 'https://primary-api.strum.education',
    autocapture: false,
    disable_surveys: true,
    enable_recording_console_log: true,
    enable_heatmaps: true
  });
} else {
  console.log('analytics is disabled due to non-production mode');
}

// allowed in staging and production
if (import.meta.env.VITE_ENV != 'development') {
  initializeFaro({
    url: `https://secondary-api.strum.education/collect`,
    apiKey: '5yAkB3B7q766hmEzx6',
    app: {
      name: 'strum',
      release: import.meta.env.BUILD_DATE,
      environment:
        window.location.hostname == 'strum.education'
          ? 'production'
          : window.location.hostname.includes('edumagick.com')
            ? 'staging'
            : 'dev'
    },
    instrumentations: [
      ...getWebInstrumentations({
        enablePerformanceInstrumentation: false,
        captureConsole: true,
        captureConsoleDisabledLevels: []
      }),
      new FetchInstrumentation(),
      new XHRInstrumentation()
    ],
    view: {
      name: window.location.href
    },
    user: {
      _id: store.state.userdata._id,
      email: store.state.userdata.email,
      attributes: { role: store.state.role }
    },
    sessionTracking: {
      enabled: true,
      persistent: true
    },
    logArgsSerializer: (args) => args.map((arg) => JSON.stringify(arg)).join(' ')
  });
} else {
  console.log('faro is disabled due to development mode');
}

let uuid = 0;
app.mixin({
  beforeCreate() {
    const ns = getCurrentInstance()?.type?.ns;
    //if (ns) tolgee.addActiveNs(ns)
    this.$uuid = uuid.toString();
    //this.ns = ns
    uuid += 1;
  },
  methods: {
    $ct(str, params) {
      const translation = this.$t(str, { /*ns: this.ns,*/ ...params });
      if (/[^ \t]\.[^ \t]/.test(translation)) {
        window?.faro?.api?.pushError(new Error(`Missing tolgee key ${translation}`));
      }
      return translation;
    }
  },
  computed: {
    $user() {
      return store.state.userdata;
    },
    $role() {
      return store.state.role;
    },
    $subjectId() {
      return store.state.subjectId;
    },
    $locale() {
      return store.state.locale;
    },
    $subject() {
      return store.state.subject;
    },
    $course() {
      return store.state.course;
    },
    $lesson() {
      return store.state.lesson;
    }
  }
});

Object.assign(app.config.globalProperties, {
  $cmain: '#140F2E',
  $cdarkgray: '#666666',
  $cmediumgray: '#c2c2c2',
  $clightgray: '#f2f2f2',
  $blue: '#74cfe3',
  $api: { ...instance },
  $capture: (event, props) => {
    if (process.env.NODE_ENV == 'development') {
      console.log(event, props);
      return;
    }
    posthog.capture(event, props);
  },
  $posthog: posthog,
  $s3: 'https://cdn.strum.education',
  $validationPassword: {
    regexp: /^[A-Za-z0-9!#$%^*]+$/,
    text: tolgee.t('main.validation.password.text')
  },
  $validationLogin: {
    regexp: /^[a-zA-Z0-9_ ]*$/,
    text: tolgee.t('main.validation.login.text')
  },
  console: (_) => console,
  $formatDate: (string, withMinutes = false) => {
    if (string === null || string === undefined) return '—';
    const date = new Date(string);
    if (date == 'Invalid Date') return '—';
    return date.toLocaleDateString('uk-UA', {
      year: '2-digit',
      month: 'numeric',
      day: 'numeric',
      ...(withMinutes && { minute: '2-digit', hour: '2-digit' })
    });
  },
  $groupBy: (arrOfObjects, key) => {
    if (!arrOfObjects?.length) return {};
    const obj = arrOfObjects.reduce((acc, obj, i) => {
      if (typeof obj != 'object') return acc;
      const value = obj[key];
      if (!acc[value]) acc[value] = [];
      acc[value].push({ ...obj, index: i });
      return acc;
    }, {});
    return Object.entries(obj);
  },
  $answerToString(arr, type) {
    const dictionary = [
      tolgee.t('main.dictionary.letter.1'),
      tolgee.t('main.dictionary.letter.2'),
      tolgee.t('main.dictionary.letter.3'),
      tolgee.t('main.dictionary.letter.4'),
      tolgee.t('main.dictionary.letter.5'),
      tolgee.t('main.dictionary.letter.6'),
      tolgee.t('main.dictionary.letter.7'),
      tolgee.t('main.dictionary.letter.8'),
      tolgee.t('main.dictionary.letter.9'),
      tolgee.t('main.dictionary.letter.10')
    ];
    if (!Array.isArray(arr)) return '—';
    if (['choice', 'multiple'].includes(type)) return arr[0].map((e) => dictionary[e]).join(' ');
    if (['pairs', 'multiple_pairs'].includes(type)) return arr.map((e) => e.map((ee) => dictionary[ee])).join('  ');
    return arr.map((e) => e[0]).join(' ');
  },
  $download(data, type, fileName) {
    const blob = new Blob([data], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = toFilename(fileName);
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  },
  $navigateLink(url, options) {
    const { isBlank, download } = options;
    const a = document.createElement('a');
    a.href = url;
    if (isBlank) a.target = '_blank';
    if (download) a.download = download;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },
  $mapList(arr, params = {}) {
    const defaultParams = {
      value: '_id',
      label: 'name',
      info: 'description',
      icon: 'icon',
      color: 'color'
    };
    return arr?.map((e) =>
      Object.fromEntries(
        Object.entries(Object.assign(defaultParams, params)).map(([k, v]) => [k, e[typeof v == 'function' ? v(e) : v]])
      )
    );
  }
});

document.addEventListener('DOMContentLoaded', async (_) => {
  app.mount('#app');
  setTimeout((_) => document.dispatchEvent(new Event('app-rendered')), 100);
});

window.addEventListener('keydown', function (e) {
  if (e.code == 'Space' && e.target == document.body) {
    e.preventDefault();
  }
});

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
}
